import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Paper,
  IconButton,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SendIcon from '@mui/icons-material/Send';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { apiFetch, openAuthenticatedUpload } from '../../api/client';

const MEIOS_ENVIO = [
  { value: 'E-mail', label: 'E-mail' },
  { value: 'Correios (AR)', label: 'Correios (AR)' },
  { value: 'Entrega presencial', label: 'Entrega presencial' },
  { value: 'Outro', label: 'Outro' },
];

const estadoInicial = {
  ocorrencia: '',
  dataOcorrencia: '',
  prazoResposta: '',
  texto: '',
};

/**
 * Editor de notificação da solicitação.
 * Datas sincronizam com a solicitação (dataReferencia / dataPrazo) no backend ao salvar.
 */
export default function EditorNotificacaoSolicitacaoDialog({ open, solicitacao, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [registrandoEnvio, setRegistrandoEnvio] = useState(false);
  const [conteudo, setConteudo] = useState(estadoInicial);
  const [meta, setMeta] = useState(null);
  const [molduraUrl, setMolduraUrl] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [dataEnvio, setDataEnvio] = useState('');
  const [meioEnvio, setMeioEnvio] = useState('');
  const [envioRegistrado, setEnvioRegistrado] = useState(null);
  const [erro, setErro] = useState('');
  const [herdado, setHerdado] = useState({ ocorrencia: false, prazo: false });

  const solicitacaoId = solicitacao?.id;

  useEffect(() => {
    if (!open || !solicitacaoId) return;

    setLoading(true);
    setErro('');
    setEnvioRegistrado(null);

    apiFetch(`/api/contratos/solicitacoes/${solicitacaoId}/notificacao`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Erro ao carregar notificação');
        return data;
      })
      .then((data) => {
        setMeta(data.solicitacao || null);
        const dataOcorrencia =
          data.conteudo?.dataOcorrencia || data.solicitacao?.dataReferencia || '';
        const prazoResposta = data.conteudo?.prazoResposta || data.solicitacao?.dataPrazo || '';
        setHerdado({
          ocorrencia: !data.conteudo?.dataOcorrencia && !!data.solicitacao?.dataReferencia,
          prazo: !data.conteudo?.prazoResposta && !!data.solicitacao?.dataPrazo,
        });
        setConteudo({
          ocorrencia: data.conteudo?.ocorrencia || '',
          dataOcorrencia,
          prazoResposta,
          texto: data.conteudo?.texto || data.solicitacao?.descricao || '',
        });
        setMolduraUrl(data.molduraUrl || null);
        setPdfUrl(data.pdfUrl || null);
        if (data.dataEnvio) {
          setDataEnvio(data.dataEnvio);
          setMeioEnvio(data.meioEnvio || '');
          setEnvioRegistrado({ dataEnvio: data.dataEnvio, meioEnvio: data.meioEnvio });
        } else {
          setDataEnvio(new Date().toISOString().slice(0, 10));
          setMeioEnvio('E-mail');
        }
      })
      .catch((e) => setErro(e.message || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [open, solicitacaoId]);

  const persistirConteudo = async () => {
    const res = await apiFetch(`/api/contratos/solicitacoes/${solicitacaoId}/notificacao`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(conteudo),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao salvar antes do PDF');
    setHerdado({ ocorrencia: false, prazo: false });
    return data;
  };

  const salvar = async () => {
    if (!solicitacaoId) return;
    setSaving(true);
    setErro('');
    try {
      await persistirConteudo();
      onSaved?.();
    } catch (e) {
      setErro(e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const gerarPdf = async () => {
    if (!solicitacaoId) return;
    setGeneratingPdf(true);
    setErro('');
    try {
      await persistirConteudo();
      const res = await apiFetch(
        `/api/contratos/solicitacoes/${solicitacaoId}/notificacao/gerar-pdf`,
        { method: 'POST' }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao gerar PDF');
      setPdfUrl(data.pdf_url);
      if (data.pdf_url) openAuthenticatedUpload(data.pdf_url);
    } catch (e) {
      setErro(e.message || 'Erro ao gerar PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const registrarEnvio = async () => {
    if (!solicitacaoId) return;
    if (!dataEnvio || !meioEnvio) {
      setErro('Informe a data e o meio de envio');
      return;
    }
    setRegistrandoEnvio(true);
    setErro('');
    try {
      const res = await apiFetch(
        `/api/contratos/solicitacoes/${solicitacaoId}/notificacao/registrar-envio`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dataEnvio, meioEnvio }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao registrar envio');
      setEnvioRegistrado({ dataEnvio: data.dataEnvio, meioEnvio: data.meioEnvio });
      onSaved?.();
    } catch (e) {
      setErro(e.message || 'Erro ao registrar envio');
    } finally {
      setRegistrandoEnvio(false);
    }
  };

  const destinoLabel = solicitacao?.subtipo === 'Fiscal' ? 'Fiscal' : 'Prestador';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Gerar Notificação — {solicitacao?.codigo || ''}</Typography>
          <IconButton onClick={onClose} size="small" aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {erro && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>
                {erro}
              </Alert>
            )}

            <Alert severity="info" sx={{ mb: 2 }}>
              Destino: <strong>{destinoLabel}</strong>
              {meta?.titulo ? ` · ${meta.titulo}` : ''}
              . Datas abaixo são as mesmas da solicitação; ao salvar, a listagem é atualizada.
            </Alert>

            {(herdado.ocorrencia || herdado.prazo) && (
              <StackChipsHerdado herdado={herdado} />
            )}

            {!molduraUrl && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Moldura institucional não encontrada. Você ainda pode salvar o texto; configure a moldura
                para gerar o PDF com timbrado.
              </Alert>
            )}

            {molduraUrl && (
              <Paper variant="outlined" sx={{ p: 1, mb: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Moldura institucional
                </Typography>
                <img
                  src={molduraUrl}
                  alt="Moldura"
                  style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain' }}
                />
              </Paper>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Data da ocorrência"
                  type="date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={conteudo.dataOcorrencia}
                  onChange={(e) => {
                    setConteudo((p) => ({ ...p, dataOcorrencia: e.target.value }));
                    setHerdado((h) => ({ ...h, ocorrencia: false }));
                  }}
                  helperText="Equivale à data de referência da solicitação"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Prazo para resposta"
                  type="date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={conteudo.prazoResposta}
                  onChange={(e) => {
                    setConteudo((p) => ({ ...p, prazoResposta: e.target.value }));
                    setHerdado((h) => ({ ...h, prazo: false }));
                  }}
                  helperText="Equivale ao prazo da solicitação (lembrete automático)"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Ocorrência"
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  value={conteudo.ocorrencia}
                  onChange={(e) => setConteudo((p) => ({ ...p, ocorrencia: e.target.value }))}
                  placeholder="Descreva o fato que motiva a notificação"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Texto da notificação"
                  fullWidth
                  multiline
                  minRows={5}
                  size="small"
                  value={conteudo.texto}
                  onChange={(e) => setConteudo((p) => ({ ...p, texto: e.target.value }))}
                  placeholder="Corpo do documento que será impresso no PDF"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" gutterBottom>
              Registrar envio
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Data do envio"
                  type="date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={dataEnvio}
                  onChange={(e) => setDataEnvio(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Meio de envio</InputLabel>
                  <Select
                    label="Meio de envio"
                    value={meioEnvio}
                    onChange={(e) => setMeioEnvio(e.target.value)}
                  >
                    {MEIOS_ENVIO.map((m) => (
                      <MenuItem key={m.value} value={m.value}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={
                    registrandoEnvio ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />
                  }
                  disabled={registrandoEnvio || saving}
                  onClick={registrarEnvio}
                  sx={{ textTransform: 'none' }}
                >
                  Registrar
                </Button>
              </Grid>
            </Grid>
            {envioRegistrado && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Envio registrado em {envioRegistrado.dataEnvio}
                {envioRegistrado.meioEnvio ? ` via ${envioRegistrado.meioEnvio}` : ''}.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
        {pdfUrl && (
          <Button
            startIcon={<OpenInNewIcon />}
            onClick={() => openAuthenticatedUpload(pdfUrl)}
            sx={{ textTransform: 'none', mr: 'auto' }}
          >
            Abrir PDF
          </Button>
        )}
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Fechar
        </Button>
        <Button
          variant="outlined"
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          disabled={loading || saving || generatingPdf}
          onClick={salvar}
          sx={{ textTransform: 'none' }}
        >
          Salvar
        </Button>
        <Button
          variant="contained"
          startIcon={generatingPdf ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
          disabled={loading || saving || generatingPdf}
          onClick={gerarPdf}
          sx={{ textTransform: 'none' }}
        >
          Gerar PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function StackChipsHerdado({ herdado }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
      {herdado.ocorrencia && (
        <Chip size="small" color="info" variant="outlined" label="Data da ocorrência herdada da solicitação" />
      )}
      {herdado.prazo && (
        <Chip size="small" color="info" variant="outlined" label="Prazo herdado da solicitação" />
      )}
    </Box>
  );
}
